import {
    Autocomplete,
    Box,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Warning from "@mui/icons-material/Warning";
import {useTranslation} from "react-i18next";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {FloorplanReservationItemDTO} from "crqlar-react-common/models/floorplan/floorplanReservationItem.dto";
import {getReservationDisplay} from "../floorPlan/v2/utils/reservation-utils";

// One existing reservation row — read-only, shows the table chip text.
export interface ExistingReservationDisplayRow {
    key: string;
    dateLabel: string;
    timeLabel: string;
    periodLabel: string;
    tableLabel: string;
}

// One adjustable reservation row — selectable tables + per-option conflict info.
export interface AdjustReservationDisplayRow {
    key: string;
    dateLabel: string;
    timeLabel: string;
    periodLabel: string;
    suggestedMissing: boolean;
    selectedTables: Resource[];
    options: Resource[];
    optionConflicts: Map<string, FloorplanReservationItemDTO[]>;
    selectedConflicts: FloorplanReservationItemDTO[];
    selectDisabled: boolean;
    showStatus: boolean;
}

interface Props {
    existingRows: ExistingReservationDisplayRow[];
    adjustRows: AdjustReservationDisplayRow[];
    onSelectionChange: (rowKey: string, value: Resource[]) => void;
    isLoadingAvailability: boolean;
    availabilityError: string | null;
}

// Date and Period are fixed; the Table cell (autocomplete in the adjust table) grows.
// Adjust adds an auto-width status-icon column at the end.
const EXISTING_COLUMN_TEMPLATE = "110px 110px minmax(0, 1fr)";
const ADJUST_COLUMN_TEMPLATE = "110px 110px minmax(0, 1fr) auto";

const headerCellSx = {
    fontWeight: 700,
    textTransform: "uppercase" as const,
    color: "text.secondary",
    letterSpacing: 0.5,
};

const formatConflictReason = (conflicts: FloorplanReservationItemDTO[]) =>
    conflicts.map(conflict => {
        const {guestNames, roomNames, timeRange} = getReservationDisplay({
            reservation: conflict.restaurantReservation,
            profiles: conflict.profiles,
            accommodationResources: conflict.accommodationResources,
        });
        return [guestNames, roomNames, timeRange].filter(Boolean).join(" · ");
    }).filter(Boolean).join(", ");

const EventsUpdateDialogTables = ({
                                      existingRows,
                                      adjustRows,
                                      onSelectionChange,
                                      isLoadingAvailability,
                                      availabilityError,
                                  }: Props) => {
    const {t} = useTranslation();

    return (
        <Stack gap={2.5}>
            {/* Existing reservations: read-only */}
            <Box>
                <Typography sx={{fontWeight: 700, pb: 1}}>
                    {t("Existing reservations")}
                </Typography>
                {existingRows.length === 0 ? (
                    <Typography color="text.secondary">
                        {t("No data")}
                    </Typography>
                ) : (
                    <Box sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 0.75,
                        p: 1.5,
                        bgcolor: "background.paper",
                    }}>
                        {/* Desktop column headers — hidden on mobile where rows stack. */}
                        <Box sx={{
                            display: {xs: "none", sm: "grid"},
                            gridTemplateColumns: EXISTING_COLUMN_TEMPLATE,
                            columnGap: 1.5,
                            pb: 0.75,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}>
                            <Typography variant="caption" sx={headerCellSx}>
                                {t("Date")}
                            </Typography>
                            <Typography variant="caption" sx={headerCellSx}>
                                {t("Period")}
                            </Typography>
                            <Typography variant="caption" sx={headerCellSx}>
                                {t("Table")}
                            </Typography>
                        </Box>

                        {existingRows.map((row, index) => (
                            <Box
                                key={row.key}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {xs: "1fr", sm: EXISTING_COLUMN_TEMPLATE},
                                    columnGap: {xs: 0, sm: 1.5},
                                    rowGap: {xs: 0.5, sm: 0},
                                    alignItems: "center",
                                    py: 1,
                                    borderTop: index === 0 ? 0 : "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Box>
                                    <Typography sx={{fontWeight: 700, lineHeight: 1.3}}>
                                        {row.dateLabel || "-"}
                                    </Typography>
                                    {row.timeLabel && (
                                        <Typography color="text.secondary" sx={{lineHeight: 1.3}}>
                                            {row.timeLabel}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography color="text.secondary" sx={{lineHeight: 1.3, alignSelf: "start"}}>
                                    {row.periodLabel || "-"}
                                </Typography>
                                <Typography color="text.secondary" sx={{lineHeight: 1.3}}>
                                    {row.tableLabel || "-"}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Adjust reservations: editable autocomplete + status icon */}
            {adjustRows.length > 0 && (
                <Box>
                    <Typography sx={{fontWeight: 700, pb: 1}}>
                        {t("Adjust reservations")}
                    </Typography>
                    {isLoadingAvailability && (
                        <Box sx={{display: "flex", alignItems: "center", gap: 1, color: "text.secondary", pb: 1}}>
                            <CircularProgress size={16}/>
                            <Typography variant="caption">
                                {t("Loading table availability")}
                            </Typography>
                        </Box>
                    )}
                    {availabilityError && (
                        <Typography variant="caption" color="error.main" sx={{display: "block", pb: 1}}>
                            {availabilityError}
                        </Typography>
                    )}
                    <Box sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 0.75,
                        p: 1.5,
                        bgcolor: "background.paper",
                    }}>
                        <Box sx={{
                            display: {xs: "none", sm: "grid"},
                            gridTemplateColumns: ADJUST_COLUMN_TEMPLATE,
                            columnGap: 1.5,
                            pb: 0.75,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}>
                            <Typography variant="caption" sx={headerCellSx}>
                                {t("Date")}
                            </Typography>
                            <Typography variant="caption" sx={headerCellSx}>
                                {t("Period")}
                            </Typography>
                            <Typography variant="caption" sx={headerCellSx}>
                                {t("Table")}
                            </Typography>
                            <Box/>
                        </Box>

                        {adjustRows.map((row, index) => (
                            <Box
                                key={row.key}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {xs: "1fr", sm: ADJUST_COLUMN_TEMPLATE},
                                    columnGap: {xs: 0, sm: 1.5},
                                    rowGap: {xs: 0.5, sm: 0},
                                    alignItems: "center",
                                    py: 1.25,
                                    borderTop: index === 0 ? 0 : "1px solid",
                                    borderColor: "divider",
                                    bgcolor: row.selectedConflicts.length > 0 ? "error.50" : undefined,
                                }}
                            >
                                <Box>
                                    <Typography sx={{fontWeight: 700, lineHeight: 1.3}}>
                                        {row.dateLabel || "-"}
                                    </Typography>
                                    {row.timeLabel && (
                                        <Typography color="text.secondary" sx={{lineHeight: 1.3}}>
                                            {row.timeLabel}
                                        </Typography>
                                    )}
                                    {row.suggestedMissing && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "warning.dark",
                                                display: "block",
                                                fontWeight: 700,
                                                lineHeight: 1.25,
                                                mt: 0.25,
                                            }}
                                        >
                                            {t("Suggested missing reservation")}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography color="text.secondary" sx={{lineHeight: 1.3, alignSelf: "start"}}>
                                    {row.periodLabel || "-"}
                                </Typography>
                                <Box sx={{minWidth: 0}}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={row.options}
                                        value={row.selectedTables}
                                        disabled={row.selectDisabled}
                                        onChange={(_, value) => onSelectionChange(row.key, value)}
                                        getOptionLabel={(option) => option.name || option.id || ""}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderOption={(props, option) => {
                                            const conflicts = option.id ? row.optionConflicts.get(option.id) ?? [] : [];
                                            const conflict = conflicts[0];
                                            const detail = conflict
                                                ? formatConflictReason([conflict])
                                                : "";

                                            return (
                                                <li {...props} key={option.id}>
                                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, width: "100%"}}>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={500}
                                                            color={conflict ? "error.main" : "success.main"}
                                                            sx={{flex: "0 0 auto"}}
                                                        >
                                                            {option.name || option.id || ""}
                                                        </Typography>
                                                        {conflict && (
                                                            <Typography variant="caption" color="error.main" noWrap sx={{minWidth: 0}}>
                                                                {detail}
                                                                {conflicts.length > 1 ? ` +${conflicts.length - 1}` : ""}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t("Select table")}
                                            />
                                        )}
                                        filterSelectedOptions
                                    />
                                    {row.selectedConflicts.length > 0 && (
                                        <Typography variant="caption" color="error.main" sx={{mt: 0.5, display: "block"}}>
                                            {t("Overlaps with")}: {formatConflictReason(row.selectedConflicts)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                }}>
                                    {row.showStatus && (row.selectedConflicts.length > 0
                                        ? <Warning color="error" fontSize="small"/>
                                        : <CheckCircle color="success" fontSize="small"/>)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Stack>
    );
};

export default EventsUpdateDialogTables;
